import { create } from 'zustand'

import { useDebouncedValue } from '#/shared/lib/hooks'

interface PhoneSearchState {
  query: string
  setQuery: (query: string) => void
}

/**
 * Shared search term for the phone panel: the search bar writes it, the
 * activity/favorites queries read it. Kept in a store so the input stays a
 * leaf component and the data hooks don't need it threaded through props.
 */
export const usePhoneSearchStore = create<PhoneSearchState>((set) => ({
  query: '',
  setQuery: (query) => set({ query }),
}))

/** Settle time before a typed term is sent to the server. */
const SEARCH_DEBOUNCE_MS = 300
/** Trigram indexes need ≥3 chars to be efficient, so don't query before then —
 *  shorter input is treated as no search. */
const MIN_SEARCH_CHARS = 3

/**
 * Debounce + minimum-length gate for a search box's raw input: returns the term
 * to query with, or undefined while it's empty or too short to be worth a round
 * trip. Shared by the panel-wide box and the in-thread one so the two behave
 * identically.
 */
export function useSearchTerm(raw: string): string | undefined {
  const typed = useDebouncedValue(raw, SEARCH_DEBOUNCE_MS).trim()
  return typed.length >= MIN_SEARCH_CHARS ? typed : undefined
}

/**
 * The panel-wide search term. Every phone-panel query reads it through here so
 * the list, the split search results and the tab badges agree on when a search
 * is running.
 */
export function usePhoneSearchTerm(): string | undefined {
  return useSearchTerm(usePhoneSearchStore((state) => state.query))
}
